import type { m365ChatTr, m365StepToolsTr } from './m365-chat.tr.js';

/** English twin of m365-chat.tr.ts; the same keys. */
export const m365ChatEn: typeof m365ChatTr = {
  name: 'Microsoft 365',
  pending: 'Preparing a card',
  cardFailed: 'This card could not be shown; the rest of the answer is above and below.',
  days: { today: 'Today', tomorrow: 'Tomorrow', yesterday: 'Yesterday', label: '{{day}} · {{weekday}}' },
  duration: {
    hours_one: '{{count}} hour',
    hours_other: '{{count}} hours',
    minutes_one: '{{count}} min',
    minutes_other: '{{count}} min',
    hoursMinutes: '{{hours}} h {{minutes}} min',
  },
  calendar: {
    source: 'Your Outlook calendar',
    meetings_one: '{{count}} meeting',
    meetings_other: '{{count}} meetings',
    noMeetings: 'No meetings',
    total: '{{duration}} in total',
    longestGap: 'longest gap {{from}}–{{to}}',
    free: 'Free · {{duration}}',
    allDay: 'All day',
    cancelled: 'Cancelled',
    teams: 'Teams',
    online: 'Online',
    list: 'Meetings',
  },
  attendees: {
    onlyYou: 'Just you',
    one: '{{name}}',
    oneAndYou: '{{name}} and you',
    two: '{{first}} and {{second}}',
    twoAndYou: '{{first}}, {{second}} and you',
    many_one: '{{first}}, {{second}} and {{count}} other',
    many_other: '{{first}}, {{second}} and {{count}} others',
  },
  mail: {
    label: 'Emails: {{title}}',
    source: 'Outlook · {{label}}',
    count_one: '{{count}} email',
    count_other: '{{count}} emails',
    replies_one: '{{count}} awaits a reply',
    replies_other: '{{count}} await a reply',
    groups: { reply: 'Awaiting your reply', info: 'For your information' },
    groupHints: { reply: '{{count}} · these first', info: '{{count}} · no reply needed' },
    waiting: 'Awaits a reply',
    unread: 'Unread',
    attachments: 'Attachments: {{names}}',
    rest_one: '{{count}} more email',
    rest_other: '{{count}} more emails',
    restWith_one: '{{count}} more email: {{summary}}',
    restWith_other: '{{count}} more emails: {{summary}}',
    notListed_one: '{{count}} more email is not listed.',
    notListed_other: '{{count}} more emails are not listed.',
    show: 'Show',
    hide: 'Hide',
  },
  attachment: {
    label: 'Email attachment: {{name}}',
    summary: 'Summary',
    source: 'Outlook · {{label}}',
    fromMail: 'Email from {{name}}',
    onlyHere: 'The attachment was used only in this conversation; it was not added to your documents.',
    siblings_one: 'This email has one more attachment:',
    siblings_other: 'This email has {{count}} more attachments:',
    page: 'p. {{from}}',
    pages: 'pp. {{from}}–{{to}}',
    slide: 'slide {{from}}',
    slides: 'slides {{from}}–{{to}}',
    extent: {
      pages_one: '{{count}} page',
      pages_other: '{{count}} pages',
      slides_one: '{{count}} slide',
      slides_other: '{{count}} slides',
      sheets_one: '{{count}} sheet',
      sheets_other: '{{count}} sheets',
    },
  },
  fileTypes: { pdf: 'PDF', word: 'Word', excel: 'Excel', powerpoint: 'PowerPoint', csv: 'CSV', text: 'Text', image: 'Image', archive: 'Archive', other: 'File' },
  write: {
    waiting: 'Waiting for your approval',
    kind: { mail: 'Email', meeting: 'Meeting', draft: 'Outlook draft', asNew: 'New email' },
    lock: { mail: 'Oppy never sends email without your approval', meeting: 'Oppy never sets up a meeting without your approval' },
    label: { mail: 'Email', meeting: 'Meeting' },
    fields: {
      from: 'From',
      to: 'To',
      cc: 'Cc',
      subject: 'Subject',
      body: 'Text',
      title: 'Title',
      date: 'Day',
      start: 'Start',
      duration: 'Length (minutes)',
      attendees: 'Attendees',
      note: 'Note',
    },
    noSubject: '(no subject)',
    external: 'External',
    replyTo: 'The reply goes not to the sender but to another address the email names: {{address}}',
    answered: 'Replied email · {{name}}, {{time}}',
    thread: 'Outlook adds the replied email and the earlier thread below your reply.',
    asNew: {
      quote: 'Replied email',
      attachments: "The original email's attachments are not included.",
      separate: 'It may appear as a separate conversation in Outlook.',
      asked: 'Prepared as a new email; its card is below.',
    },
    source: 'Replied email',
    draftAttachments: 'Attachments that go with the draft',
    footer: {
      mail: 'When you send it, it goes from your account to {{people}}.',
      meeting: 'When you create it, the invitation goes to {{people}} in your name.',
      alone: 'No invitation goes out; it is only added to your calendar.',
    },
    actions: {
      cancel: 'Cancel',
      edit: 'Edit',
      stopEditing: 'Stop editing',
      send: 'Send',
      create: 'Create and invite',
      createAlone: 'Create',
      sending: 'Sending…',
      creating: 'Creating…',
      remove: 'Remove {{name}}',
      add: 'Add',
      addAddress: 'Type an email address',
      pick: 'Pick another time:',
      pickTime: 'Pick {{time}} for the meeting',
      reconnect: 'Reconnect',
      asNewMail: 'Prepare as a new email',
    },
    state: {
      sent: 'Sent · {{time}}',
      sentNoTime: 'Sent',
      created: 'Created · invitations sent',
      createdAlone: 'Created · added to your calendar',
      teams: 'The Teams link is in the invitation',
      teamsAlone: 'The Teams link is in the calendar entry',
      noTeams: 'The Teams link could not be added',
      failed: { mail: 'Not sent', meeting: 'Not created' },
      unknown: {
        mail: "It is not known whether it was sent; check Sent Items in Outlook.",
        meeting: 'It is not known whether it was created; check your Outlook calendar.',
      },
      expired: { mail: 'Expired · not sent', meeting: 'Expired · not created' },
      cancelled: { mail: 'Not sent · you cancelled', meeting: 'Not created · you cancelled' },
      stopped: { mail: 'Not sent · stopped', meeting: 'Not created · stopped' },
      askAgain: 'If you like, ask again in the conversation; a new card is prepared.',
      unreadable: {
        title: 'This email cannot be answered as a reply.',
        detail: 'Microsoft could not open this email as a reply. I can send your reply as a new email.',
      },
      edited: 'Showing the version edited on the card.',
    },
    reasons: {
      draft_changed: 'The draft changed in Outlook; it was not sent.',
      verify_mismatch: "Outlook's draft turned out different from the card; it was not sent. The draft stays in Drafts.",
      microsoft_busy: 'Microsoft is very busy right now.',
      microsoft_forbidden: 'Microsoft did not allow this.',
      microsoft_not_found: { mail: 'Microsoft could not find the email.', meeting: 'Microsoft could not find the calendar.' },
      microsoft_rejected: 'Microsoft did not accept the request.',
      microsoft_failed: 'Something went wrong at Microsoft.',
      teams_unavailable: 'Your account cannot create Teams meetings.',
      timeout: 'Microsoft did not answer in time.',
      microsoft_unavailable: 'Microsoft could not be reached.',
      original_unreadable: 'Microsoft could not read the email being replied to.',
      other: 'Microsoft did not complete the request.',
    },
    pending: {
      scopes: 'A new permission is needed · reconnect',
      featureOff: { mail: 'Your company turned sending email off', meeting: 'Your company turned creating meetings off' },
      offTeam: 'Microsoft 365 is off for your team',
      off: 'Microsoft 365 is off for you',
      pilot: 'In trial mode, mail can only go to the allowed addresses',
      changed: 'This card is no longer as it was shown; look at its latest state in the conversation.',
      rateLimited: 'You reached the sending limit for this hour; try again in a while.',
      unavailable: 'The server cannot reach Microsoft right now; nothing was sent. Try again in a while.',
      reconnect: 'Your Microsoft connection needs to be renewed · reconnect',
      notConnected: 'Your Microsoft account is not connected · reconnect',
      consent: "Microsoft needs your company admin's consent for this permission.",
      lost: 'No answer came; the card shows its final state here shortly. You can also check Sent Items in Outlook.',
      lostMeeting: 'No answer came; the card shows its final state here shortly. You can also check your Outlook calendar.',
      invalid: 'A field of the edit is not valid; correct it and try again.',
      tooSoon: 'The meeting time has passed or is too close; pick another time.',
      invalidCard: 'This card could not be sent; ask again in the chat.',
    },
    meeting: {
      time: '{{from}} – {{to}}',
      teams: 'Teams meeting · the link is added automatically',
      noTeams: 'A Teams link cannot be added (your account has no Teams meetings)',
      organizer: 'organizer',
      availability: { free: 'free at this time', busy: 'busy at this time', unseen: 'calendar not visible', unchecked: 'calendar not checked' },
      notChecked: 'The calendars were not checked for this time.',
      noSchedule: 'The calendars could not be checked; whether the attendees are free is not known.',
      duration: {
        minutes_one: '{{count}} minute',
        minutes_other: '{{count}} minutes',
        hours_one: '{{count}} hour',
        hours_other: '{{count}} hours',
        hoursMinutes: '{{hours}} h {{minutes}} min',
      },
      strip: {
        title: '{{whose}}, {{when}}',
        when: '{{day}} {{part}}',
        whose: {
          '1': 'Your calendar',
          '2': 'Calendars of the two of you',
          '3': 'Calendars of the three of you',
          '4': 'Calendars of the four of you',
          '5': 'Calendars of the five of you',
          '6': 'Calendars of the six of you',
          '7': 'Calendars of the seven of you',
          '8': 'Calendars of the eight of you',
          '9': 'Calendars of the nine of you',
          '10': 'Calendars of the ten of you',
          many: 'Calendars of {{count}} people',
        },
        day: { today: 'today', tomorrow: 'tomorrow' },
        part: { morning: 'morning', afternoon: 'afternoon' },
        legend: { busy: 'busy', selected: 'chosen', alternative: 'also free' },
        cell: '{{from}}–{{to}} · {{state}}',
        state: {
          busy: 'busy',
          selected: 'chosen · everyone free',
          selectedBusy_one: 'chosen · {{count}} person busy',
          selectedBusy_other: 'chosen · {{count}} people busy',
          alternative: 'also free · everyone free',
          free: 'everyone free',
        },
      },
    },
    problems: {
      to: 'Add at least one recipient.',
      tooMany: 'An email can have at most 25 recipients.',
      duplicate: 'Each address can be added once.',
      address: 'Type a valid email address.',
      pendingAddress: 'Add the address you typed with Add, or clear it; an address not added gets nothing.',
      subject: 'The subject can be at most 255 characters.',
      subjectLine: 'The subject must be one line.',
      body: 'The text can be at most 20,000 characters.',
      title: 'Give the meeting a title of at most 255 characters.',
      date: 'Pick a valid day.',
      start: 'Pick a valid start time.',
      duration: 'The length must be between 15 and 480 minutes.',
      attendees: 'A meeting can have at most 19 attendees.',
      note: 'The note can be at most 4,000 characters.',
    },
    broken: 'This card could not be shown; nothing was sent. Ask again in the conversation to send it.',
  },
};

export const m365StepToolsEn: typeof m365StepToolsTr = {
  mcp__microsoft365__mail_ara: { running: 'Searching email', done: 'Searched email' },
  mcp__microsoft365__mail_oku: { running: 'Reading an email', done: 'Read an email' },
  mcp__microsoft365__eki_oku: { running: 'Reading an attachment', done: 'Read an attachment' },
  mcp__microsoft365__takvime_bak: { running: 'Checking the calendar', done: 'Checked the calendar' },
  mcp__microsoft365__toplanti_ayrintisi: { running: 'Checking a meeting', done: 'Checked a meeting' },
  mcp__microsoft365__uygun_saat_bul: { running: 'Looking for a free time', done: 'Found free times' },
  mcp__microsoft365__yanit_taslagi: { running: 'Preparing a reply', done: 'Prepared a reply', prepared: 'Draft written' },
  mcp__microsoft365__taslagi_gonder: { running: 'Preparing a draft', done: 'Prepared a draft', prepared: 'Draft ready' },
  mcp__microsoft365__mail_gonder: { running: 'Preparing an email', done: 'Prepared an email', prepared: 'Email ready' },
  mcp__microsoft365__toplanti_olustur: { running: 'Preparing a meeting', done: 'Prepared a meeting', prepared: 'Meeting ready' },
};
